import { useMemo, useState } from 'react'
import { useFilteredFacilities } from '../hooks/useFilteredFacilities'
import { useLocation } from '../contexts/LocationContext'
import { useData } from '../contexts/DataContext'
import { useFilters } from '../contexts/FilterContext'
import { useConfig } from '../contexts/ConfigContext'
import LoadingSpinner from '../components/common/LoadingSpinner'
import ErrorBoundary from '../components/common/ErrorBoundary'
import HealthComparison from '../components/comparison/HealthComparison'
import EducationComparison from '../components/comparison/EducationComparison'
import { getComparisonTargets } from '../utils/comparisonUtils'

function ComparisonView() {
  const { facilities, total: _total } = useFilteredFacilities()
  const { loading, locations } = useData()
  const { activeCategory } = useFilters()
  const {
    selectedDistrict,
    selectedSubcounty,
    selectedParish,
    selectedVillage,
    isViewingAllDistricts,
    isMultiDistrictMode,
  } = useLocation()

  const { getMaxLocationDepth } = useConfig()

  // Comparison controls state
  const [sortBy, setSortBy] = useState('compositeScore')
  const [sortOrder, setSortOrder] = useState('desc')
  const [showTopN, setShowTopN] = useState(10)

  // Calculate district aggregations for multi-district mode
  const districtFacilityGroups = useMemo(() => {
    if (!isViewingAllDistricts() || !facilities.length) return null

    return facilities.reduce((acc, facility) => {
      const district = facility.district
      if (!district) return acc

      if (!acc[district]) {
        acc[district] = []
      }
      acc[district].push(facility)
      return acc
    }, {})
  }, [facilities, isViewingAllDistricts])

  // Get comparison data and component
  const comparisonComponent = useMemo(() => {
    if (activeCategory === 'education') {
      return (
        <EducationComparison
          facilities={facilities}
          locations={locations}
          selectedDistrict={selectedDistrict}
          selectedSubcounty={selectedSubcounty}
          selectedParish={selectedParish}
          selectedVillage={selectedVillage}
          isViewingAllDistricts={isViewingAllDistricts}
          districtFacilityGroups={districtFacilityGroups}
          sortBy={sortBy}
          sortOrder={sortOrder}
          showTopN={showTopN}
          maxLocationDepth={getMaxLocationDepth()}
        />
      )
    }

    return (
      <HealthComparison
        facilities={facilities}
        locations={locations}
        selectedDistrict={selectedDistrict}
        selectedSubcounty={selectedSubcounty}
        selectedParish={selectedParish}
        selectedVillage={selectedVillage}
        isViewingAllDistricts={isViewingAllDistricts}
        districtFacilityGroups={districtFacilityGroups}
        sortBy={sortBy}
        sortOrder={sortOrder}
        showTopN={showTopN}
        maxLocationDepth={getMaxLocationDepth()}
      />
    )
  }, [
    activeCategory,
    facilities,
    locations,
    selectedDistrict,
    selectedSubcounty,
    selectedParish,
    selectedVillage,
    isViewingAllDistricts,
    districtFacilityGroups,
    sortBy,
    sortOrder,
    showTopN,
    getMaxLocationDepth,
  ])

  // Determine what locations to compare using the same logic as the comparison components
  const comparisonConfig = useMemo(() => {
    if (isViewingAllDistricts() && districtFacilityGroups) {
      // Multi-district mode: compare districts
      const districtTargets = Object.keys(districtFacilityGroups).map(
        districtName => ({
          id: districtName.toLowerCase(),
          name: districtName,
          code: `UG.${districtName.toUpperCase().slice(0, 3)}`,
        })
      )

      return {
        targets: districtTargets,
        type: 'districts',
        context:
          activeCategory === 'education'
            ? 'Comparing education across all districts'
            : 'Comparing all districts',
        level: 'district',
      }
    }

    return getComparisonTargets(
      locations,
      selectedDistrict,
      selectedSubcounty,
      selectedParish,
      selectedVillage,
      getMaxLocationDepth()
    )
  }, [
    locations,
    selectedDistrict,
    selectedSubcounty,
    selectedParish,
    selectedVillage,
    isViewingAllDistricts,
    districtFacilityGroups,
    activeCategory,
    getMaxLocationDepth,
  ])

  // Basic location stats calculation to determine if comparison is possible
  const hasComparisonData = useMemo(() => {
    if (!facilities.length) return false
    if (comparisonConfig.type === 'none') return false

    // For facility-level view (village selected), we can show facilities
    if (comparisonConfig.type === 'facilities') {
      return facilities.length > 0
    }

    // Check if we have comparison targets
    if (isViewingAllDistricts() && districtFacilityGroups) {
      return Object.keys(districtFacilityGroups).length > 0
    }

    // For other comparison modes, check if comparisonConfig has targets
    return comparisonConfig.targets && comparisonConfig.targets.length > 0
  }, [
    facilities,
    comparisonConfig,
    isViewingAllDistricts,
    districtFacilityGroups,
  ])

  const filtered_count = facilities.length

  // Show loading if facilities are currently being loaded
  if (loading[`facilities_${activeCategory}`] || loading.locations) {
    return (
      <div className="h-96 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="lg" className="mx-auto mb-4" />
          <p className="text-gray-600">Loading comparison data...</p>
        </div>
      </div>
    )
  }

  // Handle different comparison scenarios
  if (comparisonConfig.type === 'none') {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        <div className="text-center">
          <div className="text-4xl mb-2">📍</div>
          <p>Select a location to enable comparisons</p>
          <p className="text-sm mt-2">
            {isMultiDistrictMode
              ? 'Choose "All Districts" or select a specific district to compare sub-locations'
              : 'Choose a district, subcounty, or parish to compare sub-locations'}
          </p>
        </div>
      </div>
    )
  }

  if (comparisonConfig.type === 'facilities') {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        <div className="text-center">
          <div className="text-4xl mb-2">🏥</div>
          <p>Individual Facility View</p>
          <p className="text-sm mt-2">
            Switch to Grid view to see all facilities in this village
          </p>
        </div>
      </div>
    )
  }

  if (!hasComparisonData) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        <div className="text-center">
          <div className="text-4xl mb-2">📊</div>
          <p>No data available for comparison</p>
          <p className="text-sm mt-2">
            Not enough facilities in sub-locations for meaningful comparison
          </p>
        </div>
      </div>
    )
  }

  const getSortLabel = value => {
    if (activeCategory === 'education') {
      const labels = {
        compositeScore: 'Coverage Score',
        totalInstitutions: 'Institution Count',
        governmentRatio: 'Government Ratio',
        primaryToSecondaryRatio: 'Primary:Secondary Ratio',
        parishCoverage: 'Parish Coverage',
      }
      return labels[value] || value
    }

    const labels = {
      compositeScore: 'Overall Score',
      facilityCount: 'Facility Count',
      avgServiceCoverage: 'Service Coverage',
      avgInfrastructure: 'Infrastructure',
    }
    return labels[value] || value
  }

  return (
    <div className="space-y-6">
      {/* Comparison Header & Controls */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg">
        <h2 className="text-xl font-semibold text-gray-900 mb-3">
          📊 {comparisonConfig.context}
        </h2>

        {/* Controls */}
        {activeCategory !== 'education' ? (
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center space-x-2">
              <label
                htmlFor="sortBy"
                className="text-sm font-medium text-gray-700"
              >
                Sort by:
              </label>
              <select
                id="sortBy"
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <>
                  <option value="compositeScore">Overall Score</option>
                  <option value="facilityCount">Facility Count</option>
                  <option value="avgServiceCoverage">Service Coverage</option>
                  <option value="avgInfrastructure">Infrastructure</option>
                </>
              </select>
            </div>

            <div className="flex items-center space-x-2">
              <label
                htmlFor="sortOrder"
                className="text-sm font-medium text-gray-700"
              >
                Order:
              </label>
              <select
                id="sortOrder"
                value={sortOrder}
                onChange={e => setSortOrder(e.target.value)}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="desc">Best to Worst</option>
                <option value="asc">Worst to Best</option>
              </select>
            </div>

            <div className="flex items-center space-x-2">
              <label
                htmlFor="showTopN"
                className="text-sm font-medium text-gray-700"
              >
                Show:
              </label>
              <select
                id="showTopN"
                value={showTopN}
                onChange={e => setShowTopN(parseInt(e.target.value))}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={5}>Top 5</option>
                <option value={10}>Top 10</option>
                <option value={999}>All</option>
              </select>
            </div>
          </div>
        ) : null}
        {activeCategory !== 'education' ? (
          <div className="mt-3 text-sm text-gray-600">
            Comparing {comparisonConfig.type} • Total facilities:{' '}
            {filtered_count} • Sorted by {getSortLabel(sortBy)}
          </div>
        ) : null}
      </div>

      {/* Render charts based on category */}
      <ErrorBoundary
        title="Comparison Charts Error"
        fallbackMessage="There was an error loading the comparison charts. Please try refreshing the page or selecting a different location."
      >
        {comparisonComponent}
      </ErrorBoundary>

      {/* Footer */}
      <div className="text-sm text-gray-600 bg-white p-4 rounded border">
        <div className="flex items-center justify-between">
          <div>
            <span className="font-medium">Comparison Level:</span>{' '}
            {comparisonConfig.type}
          </div>
          <div>
            <span className="font-medium">Last Updated:</span>{' '}
            {new Date().toLocaleDateString()}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ComparisonView
